import React from 'react';
import { Button, Empty, Spin, Typography, List, Badge } from 'antd';
import { CheckOutlined, DeleteOutlined } from '@ant-design/icons';
import { useNotificationContext } from '../../contexts/notification.context';
import { NotificationStatus } from '../../shared/types/Notification';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

const { Text, Paragraph } = Typography;

interface NotificationListProps {
  onClose: () => void;
}

export function NotificationList({ onClose }: NotificationListProps) {
  const {
    notifications,
    unreadCount,
    loading,
    hasMore,
    loadMore,
    markAsRead,
    markAllAsRead,
    deleteNotification,
  } = useNotificationContext();

  // Garante um Date válido (aceita string ou Date)
  const safeDate = (value: any) => {
    if (!value) return new Date();
    const d = value instanceof Date ? value : new Date(value);
    return isNaN(d.getTime()) ? new Date() : d;
  };

  const handleMarkAsRead = async (notificationId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await markAsRead(notificationId);
    } catch {
    }
  };

  const handleDelete = async (notificationId: string, e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      await deleteNotification(notificationId);
    } catch {
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await markAllAsRead();
    } catch {
    }
  };

  const handleNotificationClick = (notification: any) => {
    if (notification.status === NotificationStatus.UNREAD) {
      markAsRead(notification.id).catch(() => {});
    }

    if (notification.data) {
      try {
        const data = typeof notification.data === 'string' ? JSON.parse(notification.data) : notification.data;
        if (data?.actionUrl) {
          onClose();
          window.location.href = data.actionUrl;
        }
      } catch {
      }
    }
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        maxHeight: 520,
        minWidth: 280,        // mantém mínimo, mas não força largura fixa
        maxWidth: 420,
      }}
    >
      {/* Header */}
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          padding: '10px 14px',
          borderBottom: '1px solid #f0f0f0',
          background: '#fff',
        }}
      >
        <Text strong style={{ fontSize: 15 }}>
          Notificações
        </Text>

        <div>
          {unreadCount > 0 && (
            <Button
              type="link"
              size="small"
              onClick={handleMarkAllAsRead}
              icon={<CheckOutlined />}
              style={{ fontSize: 13 }}
            >
              Marcar todas como lidas
            </Button>
          )}
        </div>
      </div>

      {/* Content */}
      <div style={{ overflowY: 'auto', flex: 1 }}>
        {loading && notifications.length === 0 ? (
          <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', padding: 36 }}>
            <Spin />
          </div>
        ) : notifications.length === 0 ? (
          <Empty
            description="Nenhuma notificação"
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            style={{ padding: '32px 0' }}
          />
        ) : (
          <>
            <List
              dataSource={notifications}
              renderItem={(notification: any) => {
                const createdAt = safeDate(notification.createdAt);
                const timeAgo = formatDistanceToNow(createdAt, { addSuffix: true, locale: ptBR });

                return (
                  // keep List.Item neutral (no forced width)
                  <List.Item
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    style={{
                      padding: 8,
                      borderBottom: 'none',
                      background: 'transparent',
                      cursor: 'pointer',
                    }}
                  >
                    {/* Caixa interna: controla largura do "card" sem forçar o pai */}
                    <div
                      style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                        padding: 12,
                        borderRadius: 8,
                        backgroundColor: notification.status === NotificationStatus.UNREAD ? '#eef8ff' : '#ffffff',
                        border: '1px solid #f3f6f8',
                        boxShadow: '0 2px 6px rgba(12,20,40,0.03)',
                        width: '100%',
                        maxWidth: 360,      // card mais estreito
                        alignSelf: 'flex-start', // não força o pai a redimensionar
                      }}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                        <div style={{ flex: 1, minWidth: 0 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                            <Text strong style={{ fontSize: 14, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                              {notification.title}
                            </Text>

                            {notification.status === NotificationStatus.UNREAD && (
                              <Badge dot style={{ marginLeft: 6 }} />
                            )}
                          </div>

                          <Paragraph style={{ margin: '6px 0 0 0', fontSize: 13, color: '#444' }} ellipsis={{ rows: 2 }}>
                            {notification.message}
                          </Paragraph>
                        </div>
                      </div>

                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          {timeAgo}
                        </Text>

                        <div style={{ display: 'flex', gap: 8 }}>
                          {notification.status === NotificationStatus.UNREAD && (
                            <Button
                              type="text"
                              size="small"
                              icon={<CheckOutlined />}
                              onClick={(e) => handleMarkAsRead(notification.id, e)}
                              style={{ padding: 4, height: 28 }}
                            />
                          )}

                          <Button
                            type="text"
                            danger
                            size="small"
                            icon={<DeleteOutlined />}
                            onClick={(e) => handleDelete(notification.id, e)}
                            style={{ padding: 4, height: 28 }}
                          />
                        </div>
                      </div>
                    </div>
                  </List.Item>
                );
              }}
            />

            {hasMore && (
              <div style={{ textAlign: 'center', padding: 8 }}>
                <Button type="link" onClick={loadMore} loading={loading}>
                  Carregar mais
                </Button>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
  